/* ============================================================
   Urban Junglist Co. - Pre-launch hero behaviour

   Phase 2: signup enhancement, Theme Editor lifecycle.
   Phase 3: entrance timeline.
   Phase 4: 2.5D pointer parallax, plant sway, waveform pulse.
   Phase 7: production polish.

   ROOT-CAUSE FIX (password modal footer flash):
   .footer-row is an ancestor of <password-modal>, which contains
   details.password-modal[open]{position:fixed;inset:0}. A transformed
   ancestor becomes the containing block for its position:fixed
   descendants, so while GSAP held an inline transform on .footer-row the
   opened modal resolved against that small bottom-anchored <ul> instead
   of the viewport - rendering the card down in the footer. Because
   fromTo defaults to immediateRender:true, that transform was written
   the instant the timeline was built and persisted until the timeline's
   onComplete clearProps at ~2.2s, at which point the modal snapped to
   centre. That gap was the visible flash.

   The footer entrance is therefore opacity-only now: autoAlpha touches
   only opacity and visibility, never transform, so .footer-row never
   becomes a containing block and the modal is correctly viewport-
   positioned from its first frame. clearProps for the footer is
   likewise narrowed to opacity,visibility - it no longer expects or
   clears a transform that is never written.

   Anything nested inside .signal-password-footer must stay
   transform-free for as long as the modal can open. If a footer motion
   effect is ever wanted again, animate the individual .list-social__item
   children rather than the .footer-row ancestor - but note the padlock's
   own <li> is an ancestor of the modal too, so exclude that one.

   GSAP owns every transform in the hero. Plant sway uses rotation and
   the waveform pulse uses scale and opacity, while parallax uses x and y,
   so the loops and the pointer response never contend for the same
   property on the same element.
   ============================================================ */

(function () {
  'use strict';

  var instances = new WeakMap();

  /* Parallax depth in pixels at full pointer deflection. Back of the
     scene moves least, foreground most. Content and CTA are absent on
     purpose: nothing the visitor is trying to click ever moves. */
  var DEPTHS = {
    background: 6,
    scene: 12,
    buildings: 9,
    plantsBack: 12,
    decks: 15,
    plantsFront: 18,
    logo: 10,
    waveforms: 13,
    rubble: 20
  };

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function hasFinePointer() {
    return !!(window.matchMedia && window.matchMedia('(pointer: fine)').matches);
  }

  function UjcPrelaunch(section) {
    this.section = section;
    this.listeners = [];
    this.loops = [];
    this.quick = [];
    this.entrance = null;
    this.resizeObserver = null;
    this.intersectionObserver = null;
    this.inView = true;
    this.viewportWidth = window.innerWidth || 1;
    this.viewportHeight = window.innerHeight || 1;

    this.layers = {
      base: this.find('.ujc-scene__layer--base'),
      buildings: this.find('.ujc-scene__layer--buildings'),
      plantsBack: this.find('.ujc-scene__layer--plants-back'),
      decks: this.find('.ujc-scene__layer--decks'),
      plantsFront: this.find('.ujc-scene__layer--plants-front'),
      logo: this.find('.ujc-scene__layer--logo'),
      waveforms: this.find('.ujc-scene__layer--waveforms'),
      rubble: this.find('.ujc-scene__layer--rubble')
    };

    this.init();
  }

  UjcPrelaunch.prototype.find = function (selector) {
    return this.section.querySelector(selector);
  };

  UjcPrelaunch.prototype.init = function () {
    this.initJoin();
    this.initEntrance();
    this.watchReducedMotion();
  };

  /* ---------- Signup ---------- */

  UjcPrelaunch.prototype.initJoin = function () {
    var self = this;
    var wraps = this.section.querySelectorAll('.ujc-join');

    Array.prototype.forEach.call(wraps, function (wrap) {
      var btn = wrap.querySelector('.ujc-join__btn');
      var input = wrap.querySelector('.ujc-join__input');
      if (!btn || !input) return;

      var onActivate = function () {
        wrap.setAttribute('data-state', 'open');
        input.focus({ preventScroll: true });
      };

      btn.addEventListener('click', onActivate);
      self.listeners.push({ el: btn, type: 'click', fn: onActivate });

      if (wrap.getAttribute('data-state') === 'open') {
        input.focus({ preventScroll: true });
      }
    });
  };

  /* ---------- Entrance ---------- */

  UjcPrelaunch.prototype.initEntrance = function () {
    var self = this;
    var root = document.documentElement;
    var pending = root.getAttribute('data-ujc-anim') === 'pending';

    if (window.ujcAnimFailsafe) {
      window.clearTimeout(window.ujcAnimFailsafe);
      window.ujcAnimFailsafe = null;
    }

    var lib = window.gsap;

    if (!pending || !lib) {
      root.setAttribute('data-ujc-anim', 'done');
      this.initScene();
      this.initParallax();
      return;
    }

    var bg = this.find('.ujc-prelaunch__bg-img');
    var scene = this.find('.ujc-scene');
    var message = this.find('.ujc-prelaunch__message');
    var supporting = this.find('.ujc-prelaunch__supporting');
    var join = this.find('.ujc-join');
    var footer = document.querySelector('.signal-password-footer .footer-row');
    var L = this.layers;

    /* Hero elements: these are animated with transforms, so their
       transform is cleared on completion along with opacity/visibility.
       The footer is deliberately NOT in this list - see below. */
    var handBack = [message, supporting, join].filter(Boolean);

    var tl = lib.timeline({
      defaults: { ease: 'power3.out' },
      onComplete: function () {
        root.setAttribute('data-ujc-anim', 'done');

        if (handBack.length) {
          lib.set(handBack, { clearProps: 'opacity,visibility,transform' });
        }

        /* Footer: opacity and visibility only. No transform is ever
           written to .footer-row, so none is cleared - clearing a
           transform here would imply this element is transform-animated,
           which must not happen while it is an ancestor of the password
           modal. */
        if (footer) {
          lib.set(footer, { clearProps: 'opacity,visibility' });
        }

        if (scene) {
          lib.set(scene, { clearProps: 'opacity,visibility' });
        }

        self.initScene();
        self.initParallax();
      }
    });

    if (bg) {
      tl.fromTo(bg, { scale: 1.075 }, { scale: 1.04, duration: 2.2, ease: 'sine.inOut' }, 0);
    }

    if (scene) {
      tl.fromTo(scene, { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 1 }, 0.1);
    }

    if (L.plantsBack) {
      tl.fromTo(L.plantsBack, { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: 0.8 }, 0.35);
    }
    if (L.plantsFront) {
      tl.fromTo(L.plantsFront, { autoAlpha: 0, y: 14 }, { autoAlpha: 1, y: 0, duration: 0.8 }, 0.5);
    }
    if (L.logo) {
      tl.fromTo(L.logo, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.7 }, 0.72);
    }
    if (L.waveforms) {
      tl.fromTo(L.waveforms, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.6 }, 0.9);
    }

    if (message) {
      tl.fromTo(message, { autoAlpha: 0, y: 8 }, { autoAlpha: 1, y: 0, duration: 0.5 }, 0.9);
    }
    if (supporting) {
      tl.fromTo(supporting, { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: 0.6 }, 1.05);
    }
    if (join) {
      tl.fromTo(join, { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: 0.6 }, 1.3);
    }

    /* Opacity-only. autoAlpha animates opacity and visibility and never
       writes a transform, so .footer-row cannot become a containing
       block for the position:fixed password modal nested inside it. */
    if (footer) {
      tl.fromTo(footer, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.6 }, 1.55);
    }

    this.entrance = tl;
  };

  /* ---------- Ambient scene loops ---------- */

  UjcPrelaunch.prototype.initScene = function () {
    var lib = window.gsap;
    if (!lib || prefersReducedMotion() || this.loops.length) return;

    var L = this.layers;

    if (L.plantsBack) {
      lib.set(L.plantsBack, { transformOrigin: '50% 86%' });
      this.loops.push(lib.fromTo(L.plantsBack,
        { rotation: -0.28 },
        { rotation: 0.28, duration: 9.5, ease: 'sine.inOut', yoyo: true, repeat: -1 }
      ));
    }

    if (L.plantsFront) {
      lib.set(L.plantsFront, { transformOrigin: '50% 90%' });
      this.loops.push(lib.fromTo(L.plantsFront,
        { rotation: 0.34 },
        { rotation: -0.34, duration: 6.8, ease: 'sine.inOut', yoyo: true, repeat: -1 }
      ));
    }

    if (L.waveforms) {
      var pulse = lib.timeline({ repeat: -1, repeatDelay: 3.4 });
      pulse
        .fromTo(L.waveforms,
          { opacity: 0.74, scale: 0.985 },
          { opacity: 1, scale: 1.015, duration: 1.1, ease: 'sine.inOut' })
        .to(L.waveforms,
          { opacity: 0.74, scale: 0.985, duration: 1.4, ease: 'sine.inOut' });
      this.loops.push(pulse);
    }
  };

  /* ---------- 2.5D pointer parallax ----------
     Every target here is inside .ujc-prelaunch. Nothing in the footer
     subtree is ever transformed, which is what keeps the password modal
     viewport-positioned. */

  UjcPrelaunch.prototype.initParallax = function () {
    var self = this;
    var lib = window.gsap;

    if (!lib || this.quick.length) return;
    if (prefersReducedMotion()) return;
    if (!hasFinePointer()) return;

    var L = this.layers;
    var targets = [
      { el: this.find('.ujc-prelaunch__bg-img'), depth: DEPTHS.background },
      { el: L.buildings, depth: DEPTHS.buildings },
      { el: L.plantsBack, depth: DEPTHS.plantsBack },
      { el: L.decks, depth: DEPTHS.decks },
      { el: L.plantsFront, depth: DEPTHS.plantsFront },
      { el: L.logo, depth: DEPTHS.logo },
      { el: L.waveforms, depth: DEPTHS.waveforms },
      { el: L.rubble, depth: DEPTHS.rubble }
    ].filter(function (t) { return t.el; });

    if (!L.base) {
      var scene = this.find('.ujc-scene');
      if (scene) targets.push({ el: scene, depth: DEPTHS.scene });
    }

    if (!targets.length) return;

    this.quick = targets.map(function (t) {
      return {
        depth: t.depth,
        setX: lib.quickTo(t.el, 'x', { duration: 0.7, ease: 'power3.out' }),
        setY: lib.quickTo(t.el, 'y', { duration: 0.7, ease: 'power3.out' })
      };
    });

    var onPointerMove = function (event) {
      if (document.hidden || !self.inView) return;

      var nx = (event.clientX / self.viewportWidth - 0.5) * 2;
      var ny = (event.clientY / self.viewportHeight - 0.5) * 2;

      for (var i = 0; i < self.quick.length; i++) {
        var q = self.quick[i];
        q.setX(-nx * q.depth);
        q.setY(-ny * q.depth);
      }
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    this.listeners.push({ el: window, type: 'pointermove', fn: onPointerMove });

    var onPointerLeave = function () { self.recentre(); };
    document.addEventListener('pointerleave', onPointerLeave, { passive: true });
    this.listeners.push({ el: document, type: 'pointerleave', fn: onPointerLeave });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(function () {
        self.viewportWidth = window.innerWidth || 1;
        self.viewportHeight = window.innerHeight || 1;
      });
      this.resizeObserver.observe(this.section);
    }

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(function (entries) {
        self.inView = entries[0].isIntersecting;
        if (!self.inView) self.recentre();
      }, { threshold: 0 });
      this.intersectionObserver.observe(this.section);
    }

    var onVisibility = function () {
      if (document.hidden) self.recentre();
    };
    document.addEventListener('visibilitychange', onVisibility);
    this.listeners.push({ el: document, type: 'visibilitychange', fn: onVisibility });
  };

  UjcPrelaunch.prototype.recentre = function () {
    for (var i = 0; i < this.quick.length; i++) {
      this.quick[i].setX(0);
      this.quick[i].setY(0);
    }
  };

  /* ---------- Runtime reduced-motion changes ---------- */

  UjcPrelaunch.prototype.watchReducedMotion = function () {
    if (!window.matchMedia) return;

    var self = this;
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    var onChange = function () {
      if (!mq.matches) return;
      self.killAmbient();
    };

    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      this.listeners.push({ el: mq, type: 'change', fn: onChange });
    } else if (mq.addListener) {
      mq.addListener(onChange);
      this.mediaLegacy = { mq: mq, fn: onChange };
    }
  };

  /* ---------- Teardown ---------- */

  UjcPrelaunch.prototype.killAmbient = function () {
    var lib = window.gsap;

    this.loops.forEach(function (loop) { loop.kill(); });
    this.loops = [];

    this.recentre();
    this.quick = [];

    if (lib) {
      var resettable = [
        this.layers.plantsBack,
        this.layers.plantsFront,
        this.layers.waveforms
      ].filter(Boolean);
      if (resettable.length) {
        lib.set(resettable, { clearProps: 'transform,opacity' });
      }
    }
  };

  UjcPrelaunch.prototype.destroy = function () {
    this.listeners.forEach(function (l) {
      if (l.el.removeEventListener) l.el.removeEventListener(l.type, l.fn);
    });
    this.listeners = [];

    if (this.mediaLegacy) {
      this.mediaLegacy.mq.removeListener(this.mediaLegacy.fn);
      this.mediaLegacy = null;
    }

    this.loops.forEach(function (loop) { loop.kill(); });
    this.loops = [];
    this.quick = [];

    if (this.entrance) {
      this.entrance.kill();
      this.entrance = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  };

  /* ---------- Mounting ---------- */

  function mount(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll('.ujc-prelaunch');

    Array.prototype.forEach.call(sections, function (el) {
      if (instances.has(el)) return;
      instances.set(el, new UjcPrelaunch(el));
    });
  }

  function unmount(el) {
    var instance = instances.get(el);
    if (!instance) return;
    instance.destroy();
    instances.delete(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(); });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', function (event) {
    document.documentElement.setAttribute('data-ujc-anim', 'done');
    mount(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var stale = event.target.querySelectorAll('.ujc-prelaunch');
    Array.prototype.forEach.call(stale, unmount);
    if (event.target.classList && event.target.classList.contains('ujc-prelaunch')) {
      unmount(event.target);
    }
  });
})();
